import { useEffect, useState } from 'react';

import { useForm, Controller, type SubmitHandler } from 'react-hook-form';
import PhoneInput, { type CountryData } from 'react-phone-input-2';
import { useNavigate, useParams } from 'react-router-dom';

import 'react-phone-input-2/lib/style.css';
import CrudLayout from '../../../layout/CRUDLayout';
import Dropdown from '../../../ui-components/Dropdown';
import StatusSelect from '../../../ui-components/StatusSelect';
import { useLazyGetRoleListQuery } from '../../roleManagement/role/roleApi';
import { useAppSelector } from '../../../hooks';
import { selectUser } from '../../user/profileSlice';

import {
  useUpdateAdminMutation,
  useInsertAdminMutation,
  useLazyGetAdminByIdQuery,
  useCheckAdminEmailMutation,
  useCheckAdminPhoneNumberMutation,
} from './adminApi';

import type { Inputs, RoleRecord } from './model';

interface AdminFormProps {
  embedded?: boolean;
  adminId?: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

const AdminForm = ({
  embedded = false,
  adminId,
  onClose,
  onSuccess,
}: AdminFormProps) => {
  const { id: routeId } = useParams<{ id?: string }>();
  const resolvedId = adminId ?? routeId;
  const isEditMode = Boolean(resolvedId);

  const [triggerGetAdminById, { data: fetchedAdmin }] =
    useLazyGetAdminByIdQuery();
  const [updateAdmin, { isLoading: isUpdating }] = useUpdateAdminMutation();
  const [insertAdmin, { isLoading }] = useInsertAdminMutation();
  const [checkEmailApi] = useCheckAdminEmailMutation();
  const [checkPhoneApi] = useCheckAdminPhoneNumberMutation();
  const [getOptions] = useLazyGetRoleListQuery();
  const [option, setOptions] = useState<RoleRecord[]>([]);
  const [role, setRole] = useState<number[]>([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [employeeRoleId, setEmployeeRoleId] = useState<number | null>(null);

  const navigate = useNavigate();

  const user = useAppSelector(selectUser);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    watch,
  } = useForm<Inputs>();

  if (!isEditMode && user) {
    // User site/building/floor logic removed as master module is deleted
  }

  useEffect(() => {
    if (isEditMode && resolvedId) {
      triggerGetAdminById(+resolvedId);
    }
  }, [isEditMode, resolvedId, triggerGetAdminById]);

  const resolveEmployeeRoleId = (roles: RoleRecord[] = []) => {
    const byCode = roles.find((item) => item.groupCode === 'EMP');
    if (byCode?.groupId) return Number(byCode.groupId);
    const byName = roles.find(
      (item) => item.groupName?.toLowerCase() === 'employee'
    );
    return byName?.groupId ? Number(byName.groupId) : null;
  };

  const enforceEmployeeRole = (roles: number[]) => {
    if (!employeeRoleId) return roles;
    const next = roles.includes(employeeRoleId)
      ? roles
      : [employeeRoleId, ...roles];
    return Array.from(new Set(next));
  };

  useEffect(() => {
    async function load() {
      const res = await getOptions().unwrap();
      const records =
        (res?.data as any)?.records || (res?.data as any)?.data || [];
      setOptions(records);
      setEmployeeRoleId(resolveEmployeeRoleId(records));
    }
    load();
  }, [getOptions]);

  useEffect(() => {
    if (!fetchedAdmin?.data) return;

    const loadAdminData = async () => {
      checkEmailApi({
        email: fetchedAdmin?.data?.email,
        adminId: fetchedAdmin?.data?.adminId,
      });
      checkPhoneApi({
        phoneNumber: fetchedAdmin?.data?.phoneNumber,
        adminId: fetchedAdmin?.data?.adminId,
      });
      const user = fetchedAdmin.data;
      const dialCode = user.dialCode || '';
      const phoneNumber = user.phoneNumber || '';
      const selectedGroups =
        fetchedAdmin?.data?.groupIds?.length &&
        Array.isArray(fetchedAdmin.data.groupIds)
          ? fetchedAdmin.data.groupIds
          : [fetchedAdmin?.data?.groupId].filter(Boolean);
      setRole(selectedGroups as number[]);

      // Reset form with all values after options are loaded
      reset({
        name: user.name || '',
        email: user.email || '',
        phoneNumber,
        status: 'Active',
        groupId: user.groupId || 0,
        dialCode,
      });
    };

    loadAdminData();
  }, [checkEmailApi, checkPhoneApi, fetchedAdmin, reset]);

  useEffect(() => {
    if (!employeeRoleId) return;
    if (isEditMode) {
      setRole((prev) => enforceEmployeeRole(prev));
    } else if (role.length === 0) {
      setRole([employeeRoleId]);
    }
  }, [employeeRoleId, isEditMode, role.length]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setFormSubmitted(true);
    if (!role.length) {
      return;
    }
    try {
      const payload = {
        ...data,
        groupId: enforceEmployeeRole(role)[0] ?? 0,
        groupIds: enforceEmployeeRole(role),
      };
      if (isEditMode && resolvedId) {
        await updateAdmin({
          id: +resolvedId,
          ...payload,
        }).unwrap();
      } else {
        await insertAdmin(payload).unwrap();
      }
      if (embedded) {
        onSuccess?.();
        onClose?.();
      } else {
        navigate('/admins');
      }
    } catch (err: any) {
      console.error(err);
    }
  };

  const formFields = (
    <>
      <div className="mb-4">
        <label htmlFor="name" className="label">
          Name <span className="required">*</span>
        </label>
        <input
          id="name"
          {...register('name', {
            required: 'Name is required',
          })}
          className="input disabled:bg-neutral-100 disabled:cursor-not-allowed"
          placeholder="Name"
          autoFocus={!isEditMode}
          disabled={isEditMode}
        />
        {errors.name && <p className="error-msg">{errors.name.message}</p>}
      </div>

      <div className="mb-4">
        <label htmlFor="email" className="label">
          Email <span className="required">*</span>
        </label>
        <input
          id="email"
          type="email"
          {...register('email', {
            required: 'Email is required',
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Invalid email format',
            },
            validate: async (value) => {
              const res = await checkEmailApi({
                email: value,
                adminId: resolvedId ? +resolvedId : 0,
              });
              if ('data' in res && (res.data as any)?.data === false) {
                return 'Email already in use';
              }
              return true;
            },
          })}
          className="input disabled:bg-neutral-100 disabled:cursor-not-allowed"
          placeholder="Email"
          disabled={isEditMode}
        />
        {errors.email && <p className="error-msg">{errors.email.message}</p>}
      </div>

      <div className="mb-4">
        <label htmlFor="phoneNumber" className="label">
          Mobile Number
        </label>
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            validate: async (value) => {
              if (!value) return true;
              const res = await checkPhoneApi({
                phoneNumber: value,
                adminId: resolvedId ? +resolvedId : 0,
              });
              if ('data' in res && (res.data as any)?.data === false) {
                return 'Mobile Number is already in use';
              }
              return true;
            },
          }}
          render={({ field }) => (
            <PhoneInput
              country="in"
              value={field.value}
              onChange={(value, data: CountryData) => {
                field.onChange(value);
                if ('dialCode' in data) {
                  setValue('dialCode', `+${data.dialCode}`);
                }
              }}
              enableSearch
              inputStyle={{
                width: '100%',
                height: '40px',
                paddingLeft: '48px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                color: '#111827',
              }}
              buttonStyle={{
                borderTopLeftRadius: '8px',
                borderBottomLeftRadius: '8px',
                width: '40px',
                backgroundColor: 'transparent',
                border: 'none',
              }}
              containerClass="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          )}
        />
        {errors.phoneNumber && (
          <p className="error-msg">{errors.phoneNumber.message}</p>
        )}
      </div>

      <div className="mb-4">
        <Dropdown
          label="Role"
          value={role}
          setValue={(next) => setRole(enforceEmployeeRole(next as number[]))}
          options={option.map((opt) => ({
            ...opt,
            isFixed: opt.groupId === employeeRoleId,
          }))}
          setOptions={(next: any) => setOptions(next)}
          labelKey="groupName"
          valueKey="groupId"
          isMulti={true}
          required={true}
          placeholder="Role"
          isSearchable={true}
          isCreatable={false}
        />
        {formSubmitted && !role.length && (
          <p className="error-msg -mt-4 ml-1">
            Please enter a value for the Role
          </p>
        )}
      </div>

      <div className="mb-4">
        <StatusSelect
          label="Status"
          name="status"
          control={control}
          error={errors.status as any}
        />
      </div>
    </>
  );

  if (embedded) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {formFields}
        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800 mt-4">
          <button
            type="button"
            onClick={() => (onClose ? onClose() : navigate('/admins'))}
            disabled={isLoading || isUpdating}
            className="px-6 py-2.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isUpdating}
            className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all text-neutral-700 tracking-wide ${
              isLoading || isUpdating
                ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 cursor-not-allowed'
                : 'bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40'
            }`}
          >
            Save
          </button>
        </div>
      </form>
    );
  }

  return (
    <CrudLayout<Inputs>
      topTitle="Manage Admin"
      title={isEditMode ? 'Update Admin' : 'Add Admin'}
      backTo="/admins"
      onSubmit={onSubmit}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      isUpdating={isUpdating}
    >
      {formFields}
    </CrudLayout>
  );
};

export default AdminForm;
